import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-white mt-8 mb-3">
    {children}
  </h2>
);

const Paragraph = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm sm:text-base leading-relaxed text-gray-200/90">{children}</p>
);

const List = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc pl-5 space-y-2 text-sm sm:text-base text-gray-200/90">{children}</ul>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10"
    >
      <Helmet>
        <title>Privacy Policy | Fantasy Fiesta</title>
        <meta name="description" content="Learn how Fantasy Fiesta collects, uses, and safeguards your information." />
        <link rel="canonical" href="https://fantasy-fiesta.com/privacy-policy" />
      </Helmet>
      <div className="rounded-3xl border border-white/10 bg-black/70 backdrop-blur p-6 sm:p-8 shadow-xl">
        <header className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Privacy Policy</h1>
          <p className="text-xs sm:text-sm text-gray-400 mt-2">Last updated: {new Date().toLocaleDateString()}</p>
        </header>

        <Paragraph>
          Fantasy Fiesta helps you analyze fantasy football drafts, rosters, and potential trades. This
          Privacy Policy explains what we collect, how we use it, and the choices you have. By using
          Fantasy Fiesta, you consent to this policy.
        </Paragraph>

        <SectionHeading>Scope</SectionHeading>
        <Paragraph>
          This policy applies to our website and application, including features like the draft assistant,
          player rankings, trade calculator, and integrations you choose to connect (for example, your
          Sleeper leagues).
        </Paragraph>

        <SectionHeading>Information We Collect</SectionHeading>
        <Paragraph>We collect the following categories of information:</Paragraph>
        <List>
          <li>
            <span className="font-medium text-white">Account information</span> such as name, email,
            and authentication identifiers when you sign in.
          </li>
          <li>
            <span className="font-medium text-white">League and roster data from integrations</span>
            you authorize (e.g., Sleeper): leagues, teams, rosters, draft results, league settings, and
            related metadata. We access this only with your permission and solely to provide features.
          </li>
          <li>
            <span className="font-medium text-white">Usage and device information</span> such as pages
            viewed, feature interactions, performance metrics, and basic device details for analytics and
            reliability.
          </li>
          <li>
            <span className="font-medium text-white">Cookies and local storage</span> used to keep you
            signed in, remember preferences, and improve the experience.
          </li>
        </List>

        <SectionHeading>How We Collect Information</SectionHeading>
        <List>
          <li>
            <span className="font-medium text-white">Directly from you</span> when you create an account
            or interact with the app.
          </li>
          <li>
            <span className="font-medium text-white">From third parties with your consent</span> when you
            connect an integration like Sleeper.
          </li>
          <li>
            <span className="font-medium text-white">Automatically</span> through cookies, local storage,
            and analytics.
          </li>
        </List>

        <SectionHeading>How We Use Information</SectionHeading>
        <List>
          <li>Provide core features (draft recommendations, trade valuations, roster insights).</li>
          <li>Personalize rankings, suggestions, and content based on your leagues and preferences.</li>
          <li>Maintain security, prevent abuse, and debug issues.</li>
          <li>Measure performance and improve the product.</li>
          <li>Communicate service updates and respond to inquiries.</li>
        </List>

        <SectionHeading>Legal Bases (EEA/UK users)</SectionHeading>
        <Paragraph>
          Where applicable, we rely on one or more of the following legal bases: performance of a contract
          (to provide the service you requested), legitimate interests (to keep our services secure and
          improve them), and consent (for optional integrations or communications).
        </Paragraph>

        <SectionHeading>Sharing and Disclosure</SectionHeading>
        <Paragraph>
          We do not sell your personal information. We share information only as needed to operate the
          service:
        </Paragraph>
        <List>
          <li>
            <span className="font-medium text-white">Service providers</span> that host, store, analyze,
            or process data on our behalf under confidentiality obligations.
          </li>
          <li>
            <span className="font-medium text-white">Integrations you connect</span> (e.g., Sleeper) to
            the extent necessary to provide features you request.
          </li>
          <li>
            <span className="font-medium text-white">Legal and safety</span> when we believe disclosure
            is required by law or to protect rights, safety, or the integrity of our services.
          </li>
          <li>
            <span className="font-medium text-white">Business transfers</span> in connection with a
            merger, acquisition, or asset sale.
          </li>
        </List>

        <SectionHeading>Data Retention</SectionHeading>
        <Paragraph>
          We retain information for as long as needed to provide the service and for legitimate business
          purposes. You may disconnect integrations or request deletion as described below; we will delete
          or anonymize data unless we must retain it for legal reasons.
        </Paragraph>

        <SectionHeading>Your Choices and Rights</SectionHeading>
        <List>
          <li>Access, update, or delete your account information via your profile or by contacting us.</li>
          <li>Disconnect integrations (e.g., Sleeper) at any time in the app or via the third party.</li>
          <li>Opt out of non-essential emails by using unsubscribe links.</li>
          <li>Request data export or deletion via in-app settings (when available).</li>
        </List>

        <SectionHeading>Security</SectionHeading>
        <Paragraph>
          We use industry-standard safeguards to protect information. No method of transmission or storage
          is 100% secure; we work continuously to improve our protections.
        </Paragraph>

        <SectionHeading>Children</SectionHeading>
        <Paragraph>
          Fantasy Fiesta is not directed to children under 13, and we do not knowingly collect personal
          information from children.
        </Paragraph>

        <SectionHeading>International Transfers</SectionHeading>
        <Paragraph>
          We may process information in the United States and other countries. By using the service, you
          consent to such processing and transfer, subject to applicable law.
        </Paragraph>

        <SectionHeading>Changes to This Policy</SectionHeading>
        <Paragraph>
          We may update this policy from time to time. We will post the updated version here and revise the
          “Last updated” date above. If changes are material, we will provide additional notice.
        </Paragraph>

        {/* Contact section intentionally omitted until a public contact channel is available. */}
      </div>
    </motion.section>
  );
};

export default PrivacyPolicy;
